/**
 * As orientações para o jurídico (E158.F3, GUS-191, ADR-0108 §2.5–§2.6). Espelha
 * `apps/api/types/orientacoes_reservadas.py`.
 *
 * - Equipe: `GET /casos/:id/orientacoes` (a minuta de agora ou o conteúdo liberado, e a situação),
 *   `GET /casos/:id/orientacoes.pdf` (o PDF da minuta) e `POST /casos/:id/orientacoes/liberar` (a
 *   retomada da liberação, sem nova assinatura).
 * - Portal: `GET /portal/casos/:id/orientacoes` (o que foi liberado com o laudo assinado) e
 *   `GET /portal/casos/:id/orientacoes.pdf`.
 *
 * A tela não reclassifica nada: a ordem, o grau e a razão vêm do servidor.
 */
import axios, { type AxiosInstance } from 'axios';

import { rehydrateBlobError } from '@/lib/laudo';
import { nomeDoAnexo, type ArquivoBaixado } from '@/lib/http_download';

export type GrauDoRisco = 'alto' | 'medio' | 'baixo';

export type NaturezaDoAchado =
  | 'contraprova'
  | 'limite_essencial'
  | 'limite_nao_decisivo'
  | 'exigencia_legal'
  | 'diligencia_decisiva'
  | 'pendencia_normativa'
  | 'tese_alternativa'
  | 'item_nao_comprovado'
  | 'reforco';

export type ConclusaoDaFormalizacao = 'regular' | 'irregular' | 'nao_demonstrada' | 'pendente';
export type ModoDoExame = 'parte' | 'juizo';
export type SituacaoDasOrientacoes = 'com_riscos' | 'sem_riscos_no_alcance';
export type EstadoDoRisco = 'em_aberto';
export type SituacaoDoFinal = 'vigente' | 'desatualizado';
export type SituacaoDasOrientacoesDoCaso =
  | 'nao_aplicavel'
  | 'indisponivel'
  | 'minuta'
  | 'nao_liberada'
  | 'liberada';

export interface CadastroDoReservadoData {
  display_code: string;
  contrato: string | null;
  parte: string | null;
  cpf_mascarado: string | null;
  instituicao: string | null;
  composto_em: string;
}

export interface ConclusaoNasOrientacoesData {
  chave: ConclusaoDaFormalizacao;
  rotulo: string;
  proposta: boolean;
  modo: ModoDoExame;
  /** E186.F1.T04: o final, composto depois da conferência final; ausente na minuta. */
  revisada?: boolean;
}

export interface RiscoNaOrientacaoData {
  numero: number;
  id: string;
  natureza: NaturezaDoAchado;
  grau: GrauDoRisco;
  razao_do_grau: string;
  providencia: string;
  motivo: string;
  fonte: string;
  resposta: string | null;
  fundamento_atingido: string;
  requisitos: string[];
  uso_pela_parte_contraria: string;
  tambem_no_laudo: boolean;
  responsavel: string;
  estado: EstadoDoRisco;
  /** E186.F1.T03: a providência pede o que o pacote do banco não traz; ausente fora disso. */
  indisponivel?: boolean;
}

export interface PrioridadeAntesDaJuntadaData {
  ordem: number;
  providencia: string;
  riscos: number[];
}

export interface OrientacoesReservadasData {
  versao: string;
  case_id: string;
  avaliacao_id: string;
  rodada: number;
  resultado_sha256: string;
  cadastro: CadastroDoReservadoData;
  conclusao: ConclusaoNasOrientacoesData;
  situacao: SituacaoDasOrientacoes;
  cobertura: string;
  criterio_do_grau: string;
  riscos: RiscoNaOrientacaoData[];
  prioridades: PrioridadeAntesDaJuntadaData[];
}

export interface FinalDasOrientacoesData {
  id: string;
  versao: number;
  resultado_sha256: string;
  criada_em: string;
  situacao: SituacaoDoFinal;
}

export interface OrientacoesDoCasoData {
  case_id: string;
  situacao: SituacaoDasOrientacoesDoCaso;
  motivo: string | null;
  orientacoes: OrientacoesReservadasData | null;
  final: FinalDasOrientacoesData | null;
}

export interface OrientacoesDoClienteData {
  case_id: string;
  versao: number;
  liberada_em: string;
  orientacoes: OrientacoesReservadasData;
}

export type MotivoDaFalha =
  | 'sem_permissao'
  | 'nao_encontrado'
  | 'nao_aplicaveis'
  | 'sem_laudo_assinado'
  | 'nao_conferidas'
  | 'instituicao_nao_cadastrada'
  | 'desatualizado'
  | 'indisponiveis'
  | 'indisponivel';

export class OrientacoesError extends Error {
  constructor(readonly motivo: MotivoDaFalha) {
    super(motivo);
    this.name = 'OrientacoesError';
  }
}

const HTTP_FORBIDDEN = 403;
const HTTP_NOT_FOUND = 404;
const DETALHE: Record<string, MotivoDaFalha> = {
  ORIENTACOES_NAO_APLICAVEIS: 'nao_aplicaveis',
  ORIENTACOES_SEM_LAUDO_ASSINADO: 'sem_laudo_assinado',
  ORIENTACOES_NAO_CONFERIDAS: 'nao_conferidas', // E159.F2: a conferência não aprovou
  INSTITUICAO_NAO_CADASTRADA: 'instituicao_nao_cadastrada', // E170.F4.C04: o caso sem banco
  RESULTADO_DESATUALIZADO: 'desatualizado',
  ORIENTACOES_INDISPONIVEIS: 'indisponiveis',
};

function motivoDoErro(err: unknown): MotivoDaFalha {
  if (!axios.isAxiosError(err)) return 'indisponivel';
  const status = err.response?.status;
  if (status === HTTP_FORBIDDEN) return 'sem_permissao';
  if (status === HTTP_NOT_FOUND) return 'nao_encontrado';
  const detalhe: unknown = (err.response?.data as { detail?: unknown } | undefined)?.detail;
  const codigo =
    typeof detalhe === 'string' ? detalhe : (detalhe as { code?: string } | undefined)?.code;
  return (codigo !== undefined ? DETALHE[codigo] : undefined) ?? 'indisponivel';
}

/** O PDF é composto e convertido na hora: a espera é a do documento, não a do cliente padrão. */
export const BAIXAR_ORIENTACOES_TIMEOUT_MS = 180_000;

async function baixar(client: AxiosInstance, url: string): Promise<ArquivoBaixado> {
  try {
    const resposta = await client.get<Blob>(url, {
      responseType: 'blob',
      timeout: BAIXAR_ORIENTACOES_TIMEOUT_MS,
    });
    const headers = resposta.headers as Record<string, unknown>;
    return {
      blob: resposta.data,
      filename: nomeDoAnexo(headers['content-disposition'], 'orientacoes-internas.pdf'),
    };
  } catch (err) {
    throw new OrientacoesError(motivoDoErro(await rehydrateBlobError(err)));
  }
}

export async function lerOrientacoesDoCaso(
  client: AxiosInstance,
  caseId: string,
): Promise<OrientacoesDoCasoData> {
  try {
    const { data } = await client.get<OrientacoesDoCasoData>(`/casos/${caseId}/orientacoes`);
    return data;
  } catch (err) {
    throw new OrientacoesError(motivoDoErro(err));
  }
}

export function baixarMinutaDasOrientacoes(
  client: AxiosInstance,
  caseId: string,
): Promise<ArquivoBaixado> {
  return baixar(client, `/casos/${caseId}/orientacoes.pdf`);
}

export async function liberarAsOrientacoes(
  client: AxiosInstance,
  caseId: string,
): Promise<OrientacoesDoCasoData> {
  try {
    const { data } = await client.post<OrientacoesDoCasoData>(
      `/casos/${caseId}/orientacoes/liberar`,
      undefined,
      { timeout: BAIXAR_ORIENTACOES_TIMEOUT_MS },
    );
    return data;
  } catch (err) {
    throw new OrientacoesError(motivoDoErro(err));
  }
}

/** O liberado do caso, ou `null` quando nada foi liberado (minuta, JUÍZO, avaliação nova). */
export async function lerOrientacoesDoCliente(
  client: AxiosInstance,
  caseId: string,
): Promise<OrientacoesDoClienteData | null> {
  try {
    const { data } = await client.get<OrientacoesDoClienteData>(
      `/portal/casos/${caseId}/orientacoes`,
    );
    return data;
  } catch (err) {
    const detalhe = axios.isAxiosError(err)
      ? (err.response?.data as { detail?: unknown } | undefined)?.detail
      : undefined;
    if (detalhe === 'ORIENTACOES_NAO_LIBERADAS') return null;
    throw new OrientacoesError(motivoDoErro(err));
  }
}

export function baixarOrientacoesDoCliente(
  client: AxiosInstance,
  caseId: string,
): Promise<ArquivoBaixado> {
  return baixar(client, `/portal/casos/${caseId}/orientacoes.pdf`);
}

/** O link da própria página no portal: abre depois do login, para quem já tem acesso ao caso. */
export function linkRestrito(caseId: string, origem: string = window.location.origin): string {
  return `${origem}/portal/casos/${caseId}/orientacoes`;
}
